import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Radio,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { FilterSection } from './SearchAndFilters';

interface Props {
  sections: FilterSection[];
  hasActiveChips: boolean;
}

const FilterPopover: React.FC<Props> = ({ sections, hasActiveChips }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpenOrClosePopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const isFilterPopoverOpen = Boolean(anchorEl);
  const id = isFilterPopoverOpen ? 'filters-popover' : undefined;

  return (
    <>
      <IconButton
        style={{
          width: '40px',
          height: '40px',
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${hasActiveChips ? theme.custom.borderAndDivider.outlinedBorder : theme.custom.borderAndDivider.default}`,
          padding: '9px',
          backgroundColor: `${hasActiveChips ? theme.custom.status.focus : theme.custom.background.level3}`
        }}
        id="open-filters-button"
        onClick={handleOpenOrClosePopover}
      >
        <Badge variant="dot" color="primary" invisible={!hasActiveChips}>
          <FilterAltOutlinedIcon sx={{ color: 'action.active' }} id="filter-icon" />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={isFilterPopoverOpen}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Grid container flexDirection="column" wrap="nowrap" gap={1} paddingY="8px">
          {sections.map((section, index) => (
            <React.Fragment key={section.id}>
              {index > 0 && <Divider />}
              <Stack paddingX="16px">
                {!section.disableHeader &&
                  (section.header || (section.onClear && !(section.isClearDisabled ?? false))) && (
                    <Grid container justifyContent="space-between" alignItems="center" wrap="nowrap" gap={1}>
                      {!section.disableHeader && section.header && (
                        <Typography variant="caption" color="text.secondary">
                          {section.header}
                        </Typography>
                      )}
                      {section.onClear && !(section.isClearDisabled ?? false) && (
                        <Button
                          id={`${section.id}-clear-button`}
                          onClick={section.onClear}
                          variant="text"
                          sx={{
                            fontSize: theme.typography.caption.fontSize,
                            fontWeight: 'normal',
                            textDecoration: 'underline',
                            textTransform: 'none',
                            padding: 0,
                            minWidth: 0,
                            background: 'none'
                          }}
                        >
                          {translation.clear}
                        </Button>
                      )}
                    </Grid>
                  )}
                {section.customContent
                  ? section.customContent
                  : section.items?.map((item) =>
                      section.type === 'radio' ? (
                        <FormControlLabel
                          key={item.key}
                          control={
                            <Radio
                              id={`${item.key}-radio`}
                              checked={item.checked}
                              onChange={item.onChange}
                              name={section.header}
                            />
                          }
                          label={item.label}
                        />
                      ) : (
                        <FormControlLabel
                          key={item.key}
                          control={
                            <Checkbox
                              id={`${item.key}-checkbox`}
                              checked={item.checked}
                              onChange={item.onChange}
                              name={item.key}
                            />
                          }
                          label={item.label}
                        />
                      )
                    )}
              </Stack>
            </React.Fragment>
          ))}
        </Grid>
      </Popover>
    </>
  );
};

export default FilterPopover;
